import { pmt } from '@/pages/blueprint-lite/utils/finance';

import type { CalculationSettings } from '../config/calculationSettings';
import type {
  ExistingProperty,
  LiabilitiesBreakdown,
  OtherLoan,
} from '../types';

const FREQUENCY_PER_YEAR: Record<string, number> = {
  weekly: 52,
  fortnightly: 26,
  monthly: 12,
  quarterly: 4,
  yearly: 1,
  annually: 1,
};

// loan.repaymentSum is already in AUD (the mapper converts via the live FX
// feed). This function just annualises by repayment frequency and returns
// the monthly equivalent.
function normaliseLoanMonthly(loan: OtherLoan): number {
  const freq =
    FREQUENCY_PER_YEAR[loan.repaymentFrequency?.toLowerCase?.()] ?? 12;
  return (loan.repaymentSum * freq) / 12;
}

export interface LiabilitiesInput {
  creditCardLimitAud: number;
  hecsBalance: number;
  otherLoans: OtherLoan[];
  coapplicantOtherLoans: OtherLoan[];
  existingProperties: ExistingProperty[];
  hasPurchase: boolean;
  settings: CalculationSettings;
}

export function computeLiabilities(
  input: LiabilitiesInput,
): LiabilitiesBreakdown {
  const { settings } = input;
  const ccMonthly =
    (input.creditCardLimitAud * settings.ccAssessmentFactor) / 12;
  const hecsMonthly = input.hecsBalance > 0 ? settings.hecsMonthlyAud : 0;

  const otherLoansMonthly = [
    ...input.otherLoans,
    ...input.coapplicantOtherLoans,
  ].reduce((sum, loan) => sum + normaliseLoanMonthly(loan), 0);

  const existingMortgageMonthly = input.hasPurchase
    ? input.existingProperties.reduce(
        (sum, p) =>
          sum +
          (p.mortgageBalance > 0
            ? pmt(
                p.mortgageBalance,
                settings.indicativeRate,
                settings.loanTermMonths,
              )
            : 0),
        0,
      )
    : 0;

  return {
    ccMonthly,
    hecsMonthly,
    otherLoansMonthly,
    existingMortgageMonthly,
    total:
      ccMonthly + hecsMonthly + otherLoansMonthly + existingMortgageMonthly,
  };
}
